import { Action, Provider } from '../../config';
import { Routes } from '../../config/routes';
import validationConfig from './validationConfig';
import { getStorageItemForWalletNetwork, StorageKey } from '../../utils/storage';
import { RequiredDeclarations } from '../GatekeeperNetworkService/GatekeeperNetworkServiceData';

export type LandingRouteRequest = {
  networkName: string;
  networkAddress: string;
  action: Action;
  isWebFlowEnabled: boolean;
  walletAddress: string | null;
  requiredDeclarations?: RequiredDeclarations;
  credentialExpired?: boolean;
};

export type LandingRouteResult = {
  route: Routes;
  providerToSelect?: Provider;
};

const issuanceRouting = async (request: LandingRouteRequest): Promise<LandingRouteResult> => {
  const { networkName, networkAddress, isWebFlowEnabled, walletAddress } = request;

  const explainer = validationConfig.explainerMappings[networkName] || validationConfig.explainerMappings.default;
  const provider =
    validationConfig.issuanceProviderMappings[networkName] || validationConfig.issuanceProviderMappings.default;
  // Do not check the user status if the web flow is not enabled
  if (!isWebFlowEnabled) {
    return { route: explainer };
  }

  // check that the user has previously accepted the terms and biometrics policy (if it is required)
  const storedAcceptedTermsLink = getStorageItemForWalletNetwork(
    StorageKey.AcceptedTerms,
    networkAddress,
    walletAddress,
  );
  const storedAcceptedPrivacyPolicyLink = getStorageItemForWalletNetwork(
    StorageKey.AcceptedPrivacyPolicy,
    networkAddress,
    walletAddress,
  );
  const storedAcceptedBiometricsLink = getStorageItemForWalletNetwork(
    StorageKey.AcceptedBiometrics,
    networkAddress,
    walletAddress,
  );
  const acceptedTermsLinksEqual =
    storedAcceptedTermsLink === request.requiredDeclarations?.acceptedTermsAndConditionsLink;
  const acceptedPrivacyPolicyLinksEqual =
    storedAcceptedPrivacyPolicyLink === request.requiredDeclarations?.acceptedPrivacyPolicyLink;
  const acceptedBiometricLinksEqual =
    (storedAcceptedBiometricsLink || null) === (request.requiredDeclarations?.acceptedBiometricsPolicyLink || null);
  if (!acceptedTermsLinksEqual || !acceptedBiometricLinksEqual || !acceptedPrivacyPolicyLinksEqual) {
    return { route: explainer };
  }

  // otherwise set the provider and show the login screen to continue
  return { route: Routes.LOGIN, providerToSelect: provider };
};

const refreshRouting = async (request: LandingRouteRequest): Promise<LandingRouteResult> => {
  const { networkName } = request;
  const provider =
    validationConfig.refreshProviderMappings[networkName] || validationConfig.refreshProviderMappings.default;
  return { route: Routes.LOGIN, providerToSelect: provider };
};

/** Helper function to load landing route for unauthenticated user */
const getLandingRoute = async (request: LandingRouteRequest): Promise<LandingRouteResult> => {
  let { action } = request;
  if (request.credentialExpired) {
    action = Action.ISSUANCE;
  }
  switch (action) {
    case Action.REFRESH:
      return refreshRouting(request);
    default:
      return issuanceRouting(request);
  }
};

export default {
  getLandingRoute,
};
